import type { EncontroEvento } from '@/types/evento-publico';
import TextoRico from '@/components/base/TextoRico';

interface Item {
  chave: string;
  data: string;
  titulo: string;
  detalhe: string | null;
  descricao: string | null;
  obrigatorio: boolean;
  passou: boolean;
  destaque: boolean;
}

interface ItemBruto {
  chave: string;
  inicio_em: string;
  ehOEvento: boolean;
  nome: string;
  local: string | null;
  para_quem: string | null;
  descricao: string | null;
  obrigatorio: boolean;
  passou: boolean;
}

function dataComHora(iso: string): string {
  const d = new Date(iso);
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'long' });
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${data} · ${semana}, ${hora}`;
}

function dataPeriodo(inicio: string, fim: string | null): string {
  const dIni = new Date(inicio);
  const dataIni = dIni.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const semana = dIni.toLocaleDateString('pt-BR', { weekday: 'long' });

  if (fim) {
    const dFim = new Date(fim);
    if (dFim.toDateString() !== dIni.toDateString()) {
      const dataFim = dFim.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      return `${dataIni} a ${dataFim} · ${semana}`;
    }
  }

  return `${dataIni} · ${semana}`;
}

interface Props {
  encontros: EncontroEvento[];
  eventoNome: string;
  eventoInicioEm: string | null;
  eventoFimEm: string | null;
  /** Só a linha do tempo, sem a moldura de seção (usado dentro de um Accordion). */
  compacto?: boolean;
}

export default function LinhaDoTempoDatas({
  encontros,
  eventoNome,
  eventoInicioEm,
  eventoFimEm,
  compacto = false,
}: Props) {
  if (encontros.length === 0 && !eventoInicioEm) return null;

  const agora = Date.now();
  const eventoJaPassou = eventoInicioEm ? new Date(eventoInicioEm).getTime() < agora : false;

  const brutos: ItemBruto[] = [
    ...encontros.map((e) => ({
      chave: `enc-${e.id}`,
      inicio_em: e.inicio_em,
      ehOEvento: false,
      nome: e.nome,
      local: e.local_nome,
      para_quem: e.para_quem,
      descricao: e.descricao,
      obrigatorio: e.obrigatorio,
      passou: e.ja_passou,
    })),
    ...(eventoInicioEm
      ? [
          {
            chave: 'o-evento',
            inicio_em: eventoInicioEm,
            ehOEvento: true,
            nome: eventoNome,
            local: null,
            para_quem: null,
            descricao: null,
            obrigatorio: false,
            passou: eventoJaPassou,
          },
        ]
      : []),
  ].sort((a, b) => new Date(a.inicio_em).getTime() - new Date(b.inicio_em).getTime());

  const itens: Item[] = brutos.map((b) => ({
    chave: b.chave,
    data: b.ehOEvento ? dataPeriodo(b.inicio_em, eventoFimEm) : dataComHora(b.inicio_em),
    titulo: b.nome,
    detalhe: b.ehOEvento
      ? null
      : [b.local, b.para_quem ? `para a ${b.para_quem}` : null].filter(Boolean).join(' · '),
    descricao: b.descricao,
    obrigatorio: b.obrigatorio,
    passou: b.passou,
    destaque: b.ehOEvento,
  }));

  const lista = (
    <div className={compacto ? '' : 'mt-6'}>
      {itens.map((item, i) => {
            const ultimo = i === itens.length - 1;
            return (
              <div key={item.chave} className="relative pl-8">
                {!ultimo && (
                  <span className="absolute bottom-0 left-[5px] top-4 w-0.5 bg-secondary-200" />
                )}
                <span
                  className={`absolute left-0 top-1 h-3 w-3 rounded-full ${
                    item.passou ? 'border-2 border-secondary-300 bg-white' : 'bg-primary-500'
                  }`}
                />
                <div className={ultimo ? '' : 'pb-6'}>
                  <p
                    className={`text-sm font-medium ${
                      item.passou
                        ? 'text-foreground-400'
                        : item.destaque
                          ? 'text-primary-600'
                          : 'text-foreground-600'
                    }`}
                  >
                    {item.data}
                  </p>
                  <p
                    className={`mt-0.5 ${
                      item.destaque
                        ? 'text-base font-bold text-primary-600'
                        : item.passou
                          ? 'text-foreground-400'
                          : 'text-foreground-900'
                    }`}
                  >
                    {item.titulo}
                  </p>
                  {item.detalhe && (
                    <p
                      className={`mt-0.5 text-sm ${
                        item.passou ? 'text-foreground-400' : 'text-foreground-600'
                      }`}
                    >
                      {item.detalhe}
                    </p>
                  )}
                  {item.descricao && (
                    <TextoRico
                      texto={item.descricao}
                      className={`mt-1 text-sm ${
                        item.passou ? 'text-foreground-400' : 'text-foreground-600'
                      }`}
                    />
                  )}
                  {item.obrigatorio && (
                    <span
                      className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        item.passou
                          ? 'bg-secondary-100 text-secondary-500'
                          : 'bg-primary-100 text-primary-700'
                      }`}
                    >
                      obrigatória
                    </span>
                  )}
                </div>
              </div>
            );
          })}
    </div>
  );

  if (compacto) return lista;

  return (
    <section className="border-t border-secondary-200/60 bg-background-50 py-10 lg:py-14">
      <div className="mx-auto max-w-[720px] px-4 lg:px-0">
        <h2 className="text-[17px] font-extrabold text-foreground-950">Datas importantes</h2>
        <p className="mt-1 text-sm text-foreground-600">
          Os compromissos deste evento, em ordem de data.
        </p>
        {lista}
      </div>
    </section>
  );
}